import { Api } from '@/Api';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { defineStore } from 'pinia';

export const ANONYMOUS_USER_EMAIL = 'Anonymous User';

/**
 * Stat received from the API - hardcoded last 30 days
 */
export interface Stat {
  id: string;
  type: string;
  createdAt: string;
  email: string;
  viewedPc: number;
}

export interface ItemStatsState {
  itemStatsId?: number;
  list?: Stat[];
  loading: boolean;
}

const { debug } = useDebug(DEBUG_NAMESPACE.STORE_STATS);

export const useViewerStatsStore = defineStore('viewerStatsStore', {
  state: () =>
    ({
      itemStatsId: undefined,
      list: undefined,
      loading: false,
    }) as ItemStatsState,
  actions: {
    // Initialize viewer from application store initializer (Id for VIDEO TRACKING)
    initialize(data: any) {
      this.setItemStatsId(data.itemStats);
    },
    setItemStatsId(itemStats: any) {
      this.itemStatsId = itemStats?.id;
    },
    flatItemStat(data: any): Stat {
      const stat = {
        ...data,
        createdAt: data?.attributes?.createdAt || undefined,
        email: data?.attributes?.email || undefined,
        viewedPc: data?.attributes?.data?.viewedPc || undefined,
      };
      delete stat.attributes;
      return stat;
    },
    async updateItemStats({ id, percentage, duration }: { id?: string; percentage: number; duration: number }) {
      if (!id || !this.itemStatsId) {
        debug('[updateItemStats] Cannot update item stats - missing id or statsId');
        return;
      }

      return await Api.stats.update(id, { stat_id: this.itemStatsId, viewed_pc: percentage, length: duration });
    },
    async loadStats(id: string) {
      try {
        this.loading = true;
        const res = await Api.stats.get(id);
        this.list = res?.data?.stats?.map((stat: any) => this.flatItemStat(stat));
        this.loading = false;
        return res;
      } catch (e) {
        this.loading = false;
        debug('Cannot load stats data', e);
      }
    },
  },
  getters: {
    /**
     *
     * @param state this getter recreates classic viewer stats
     * @returns stats list with only the highest percentage viewed for signed in users and all stats for anonymous users
     */
    groupStats: (state: ItemStatsState) => {
      const groupedStats: { [key: string]: Stat[] } = {};

      state.list?.forEach(stat => {
        const email = stat.email;
        const percentage = stat.viewedPc || 0;

        groupedStats[email] = groupedStats[email] || [];

        if (email == ANONYMOUS_USER_EMAIL) {
          return groupedStats[email].push(stat);
        }

        // recrate classic viewer stats take only the highest percentage viewed for signed in users
        if (percentage >= (groupedStats[email][0]?.viewedPc || 0)) {
          groupedStats[email] = [{ ...stat }];
        }
      });

      return groupedStats;
    },
    // groupStats: (state: ItemStatsState) => {
    //   return state.list?.filter(stat => {
    //     const email = stat.email;
    //     const percentage = stat.viewedPc || 0;

    //     if (email == ANONYMOUS_USER_EMAIL) {
    //       return true;
    //     }

    //     if (percentage > 0) {
    //       return true;
    //     }
    //     return false;
    //   });
    // },
    groupStatsCount(): number {
      let count = this.groupStats[ANONYMOUS_USER_EMAIL]?.length || 0;
      Object.keys(this.groupStats).forEach(key => {
        if (key !== ANONYMOUS_USER_EMAIL) {
          count++;
        }
      });
      return count;
    },
    groupStatsContainsAnonymousUser: (state: ItemStatsState) => {
      return state.list?.some(stat => stat.email === ANONYMOUS_USER_EMAIL);
    },
  },
});
